import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import { toast } from 'sonner';
import { useId, useState } from 'react';
import { hasContentPreferences } from '../../../../../utils/hasContentPreferences';
import { BusinessInfoDisplay } from '../../../../components/BusinessInfoDisplay';
import { Button } from '../../../../components-v2/Button';
import { Checkbox } from '../../../../components-v2/Checkbox';
import {
	InputGroupAddon,
	InputGroupText,
	InputGroupTextarea,
	InputGroupV2,
} from '../../../../components-v2/InputGroup';
import { styleSheet } from './styles';
import { Field, FieldLabel } from '@HoudiniUI/Field';
import { GenerationFormAttachments, useGenerationAttachments } from './GenerationFormAttachments';

const CONTENT_TYPE_OPTIONS = [
	{ label: 'Email', value: 'Email' },
	{ label: 'Social Media', value: 'SocialMediaPost' },
	{ label: 'Blog', value: 'Blog' },
] as const;

const MAX_INSTRUCTIONS_LENGTH = 2000;

function getAvailableContentTypes(accountFeatures?: Partial<Api.IAccountFeatures>) {
	return CONTENT_TYPE_OPTIONS.filter(option => {
		if (option.value === 'SocialMediaPost') return accountFeatures?.socialMedia?.enabled !== false;
		if (option.value === 'Blog') return accountFeatures?.blogFeature?.enabled !== false;
		return true;
	});
}

export interface ContentCalendarGenerationRequest {
	contentTypes: string[];
	instructions: string;
	customizeExistingContent: boolean;
	fileAttachmentIds: string[];
}

interface ContentCalendarGenerationFormProps {
	onGenerate: (request: ContentCalendarGenerationRequest) => void;
	impersonationContext?: Api.IImpersonationContext;
	disabled?: boolean;
	contentProfile?: Api.AccountProfile | null;
	accountFeatures?: Partial<Api.IAccountFeatures>;
	/**
	 * Whether the account has custom branding configured. Gates the "apply their branding" toggle:
	 * the toggle is disabled (with an explanation) when no branding is set up, since there is
	 * nothing to apply. `undefined` means the branding query has not resolved yet — the toggle is
	 * disabled but the "set up branding" hint is suppressed to avoid a misleading flash.
	 */
	accountHasBrandingSettings?: boolean;
}

export function ContentCalendarGenerationForm({
	onGenerate,
	impersonationContext,
	disabled,
	contentProfile,
	accountFeatures,
	accountHasBrandingSettings,
}: ContentCalendarGenerationFormProps) {
	const availableOptions = getAvailableContentTypes(accountFeatures);
	const [selectedTypes, setSelectedTypes] = useState<string[]>(() => availableOptions.map(o => o.value));
	const [instructions, setInstructions] = useState('');
	const [preferencesExpanded, setPreferencesExpanded] = useState(false);
	const [applyBranding, setApplyBranding] = useState(false);
	const checkboxId = useId();
	const brandingCheckboxId = useId();
	const hasPreferences = hasContentPreferences(contentProfile);
	const brandingDisabled = disabled || !accountHasBrandingSettings;
	const isOverInstructionsLimit = instructions.length > MAX_INSTRUCTIONS_LENGTH;
	const attachments = useGenerationAttachments({ impersonationContext });

	const toggleType = (value: string) => {
		setSelectedTypes(prev => (prev.includes(value) ? prev.filter(t => t !== value) : [...prev, value]));
	};

	const handleSubmit = () => {
		if (selectedTypes.length === 0 || isOverInstructionsLimit) return;
		if (attachments.isUploading) {
			toast.warning('Please wait for the uploads to finish.');
			return;
		}
		onGenerate({
			contentTypes: selectedTypes,
			instructions,
			// Only send the flag when branding is actually configured — otherwise there is nothing to apply.
			customizeExistingContent: applyBranding && !!accountHasBrandingSettings,
			fileAttachmentIds: attachments.fileAttachmentIds,
		});
		attachments.clear();
	};

	return (
		<div className={css(styleSheet.generationForm)}>
			<div className={css(styleSheet.contentTypeCheckboxes)}>
				{availableOptions.map(option => {
					const id = `${checkboxId}-${option.value}`;
					return (
						<div key={option.value} className={css(styleSheet.contentTypeCheckboxRow)}>
							<Checkbox
								id={id}
								checked={selectedTypes.includes(option.value)}
								onCheckedChange={() => toggleType(option.value)}
								disabled={disabled}
							/>
							<label htmlFor={id} className={css(styleSheet.contentTypeLabel)}>
								{option.label}
							</label>
						</div>
					);
				})}
			</div>

			<div className={css(styleSheet.brandingSection)}>
				<div className={css(styleSheet.brandingRow)}>
					<Checkbox
						id={brandingCheckboxId}
						checked={applyBranding && !!accountHasBrandingSettings}
						onCheckedChange={checked => setApplyBranding(checked === true)}
						disabled={brandingDisabled}
					/>
					<label
						htmlFor={brandingCheckboxId}
						className={css(styleSheet.brandingLabel, brandingDisabled && styleSheet.brandingLabelDisabled)}
					>
						Apply their branding to suggested content
					</label>
				</div>
				{accountHasBrandingSettings === false ? (
					<span className={css(styleSheet.brandingHelperText)}>Set up custom branding in Settings to enable this.</span>
				) : null}
			</div>

			{hasPreferences ? (
				<div className={css(styleSheet.preferencesSection)}>
					<button
						type='button'
						className={css(styleSheet.preferencesToggle)}
						onClick={() => setPreferencesExpanded(prev => !prev)}
						aria-expanded={preferencesExpanded}
					>
						<span>Your content preferences</span>
						<span
							className={css(
								styleSheet.preferencesChevron,
								preferencesExpanded && styleSheet.preferencesChevronExpanded
							)}
						>
							&#9662;
						</span>
					</button>
					{preferencesExpanded ? (
						<div className={css(styleSheet.preferencesContent)}>
							<BusinessInfoDisplay profile={contentProfile} />
						</div>
					) : null}
				</div>
			) : null}

			<Field>
				<FieldLabel>
					{hasPreferences ? 'Any additional instructions?' : 'Any special directions for this content?'}
				</FieldLabel>
				<InputGroupV2>
					<InputGroupTextarea
						value={instructions}
						onChange={e => setInstructions(e.target.value)}
						placeholder={
							hasPreferences
								? 'e.g. "This client wants a humorous tone" or "Focus on spring promotions"'
								: 'e.g. "Focus on spring promotions" or "Keep the tone warm and approachable"'
						}
						aria-invalid={isOverInstructionsLimit}
						disabled={disabled}
						rows={8}
					/>
					<InputGroupAddon align='blockEnd' styles={[styleSheet.instructionsCounterAddon]}>
						<InputGroupText
							styles={[styleSheet.instructionsCounter, isOverInstructionsLimit && styleSheet.instructionsCounterOver]}
						>
							{instructions.length.toLocaleString()}/{MAX_INSTRUCTIONS_LENGTH.toLocaleString()}
						</InputGroupText>
					</InputGroupAddon>
				</InputGroupV2>
			</Field>
			<GenerationFormAttachments attachments={attachments} disabled={disabled} />

			<Button
				onClick={handleSubmit}
				disabled={disabled || selectedTypes.length === 0 || isOverInstructionsLimit || attachments.isUploading}
			>
				Generate Content Plan
			</Button>
		</div>
	);
}
